import { Collapse, Stack, Typography } from "@mui/material";
import ExpandableSection from "@/components/serviceFlow/clientDetails/expandableSection";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import useBookingServiceSelection from "@/hooks/booking/useBookingServiceSelection";
import BookingInfoCard from "../bookingInfoCard";
import DisabledServicesWarning from "../disabledServicesWarning";
import ServiceSelectionItem from "../serviceSelectionItem";

export default function BookingServicesSelection() {
  const {
    selectedServices,
    medspaData,
    customizationEnabled,
    customizationPrimaryColor,
    isSelectiveBookingLink,
    selectiveBookingLink,
  } = useClientBookingContext();
  const { disabledServiceIds, onSelect } = useBookingServiceSelection();

  const isGroupByCategoryEnabled =
    medspaData.configuration?.groupServiceMenuItemsByCategoriesEnabled;
  const hasServiceRestrictions = disabledServiceIds.length > 0;
  const themeColor = customizationEnabled ? customizationPrimaryColor : null;

  const getAvailableServices = (services) => {
    if (!isSelectiveBookingLink) return services;
    return services.filter((service) =>
      selectiveBookingLink?.services.some((s) => s.id === service.id)
    );
  };

  const renderServiceItem = (service) => (
    <ServiceSelectionItem
      key={service.id}
      onSelectionChange={() => onSelect(service)}
      isSelected={selectedServices.some(
        (selected) => selected.id === service.id
      )}
      isDisabled={disabledServiceIds.includes(service.id)}
      service={service}
      customColor={themeColor}
    />
  );

  const renderServicesByCategory = () => {
    const validCategories = medspaData.serviceCategories
      .map((category) => ({
        ...category,
        availableServices: getAvailableServices(
          category.medspaServiceMenuItems
        ),
      }))
      .filter((category) => category.availableServices.length > 0);

    return (
      <>
        {validCategories.map((category, index) => (
          <ExpandableSection
            key={category.id}
            title={category.name}
            expanded={index === 0}
          >
            <Stack sx={{ mt: 3 }}>
              {category.availableServices.map(renderServiceItem)}
            </Stack>
          </ExpandableSection>
        ))}
      </>
    );
  };

  const renderServicesList = () => (
    <>
      {getAvailableServices(medspaData.serviceMenuItems).map(renderServiceItem)}
    </>
  );

  return (
    <BookingInfoCard>
      <Typography variant="h3" sx={{ color: "text.primary", pb: 3 }}>
        {isSelectiveBookingLink
          ? "Select one service"
          : "Select one or more services"}
      </Typography>

      <Collapse in={hasServiceRestrictions && !isSelectiveBookingLink}>
        <DisabledServicesWarning />
      </Collapse>

      {isGroupByCategoryEnabled
        ? renderServicesByCategory()
        : renderServicesList()}
    </BookingInfoCard>
  );
}
